import React, { useMemo } from 'react';
import { getSupportedTokensForNetwork, getNetworkConfig } from '../config/networks';
import { trackPaymentEvent } from '../utils/analytics';

interface PaymentMethodSelectorProps {
  network: string;
  selectedToken?: string;
  onTokenChange: (token?: string) => void;
  pricePerToken: number;
  amount?: number;
  disabled?: boolean;
}

const PaymentMethodSelector: React.FC<PaymentMethodSelectorProps> = ({
  network,
  selectedToken,
  onTokenChange,
  pricePerToken,
  amount = 100,
  disabled = false
}) => {
  const networkConfig = getNetworkConfig(network);
  const supportedTokens = useMemo(() => getSupportedTokensForNetwork(network), [network]);
  
  if (!networkConfig) {
    return null;
  }

  // Calculate token amounts for display
  const calculateTokenAmount = (usdAmount: number, tokenSymbol?: string) => {
    // For stablecoins, it's usually 1:1 with USD
    if (tokenSymbol && ['USDC', 'USDT', 'BUSD'].includes(tokenSymbol)) {
      return usdAmount.toFixed(2);
    }
    
    // For native currency, we'd need real-time prices
    // This is a placeholder - in production, fetch from price feed
    const mockPrices: Record<string, number> = {
      ETH: 2000,
      MATIC: 0.8,
      BNB: 300,
      AVAX: 30,
      SOL: 40,
      BTC: 40000,
      DOT: 5,
      TRX: 0.08
    };
    
    const nativeSymbol = networkConfig.nativeCurrency?.symbol;
    const price = mockPrices[nativeSymbol || ''] || 1;
    
    return (usdAmount / price).toFixed(6);
  };

  const tokenAmount = amount / pricePerToken;

  return (
    <div className="payment-method-selector">
      <h3 className="payment-method-title">Select Payment Method</h3>
      
      <div className="payment-methods">
        {/* Native currency option */}
        <button
          onClick={() => !disabled && onTokenChange(undefined)}
          className={`payment-method ${!selectedToken ? 'selected' : ''} ${disabled ? 'disabled' : ''}`}
          disabled={disabled}
          aria-label={`Pay with ${networkConfig.nativeCurrency?.symbol}`}
        >
          <div className="payment-method-content">
            <div className="payment-method-header">
              <span className="token-symbol">
                {networkConfig.nativeCurrency?.symbol}
              </span>
              <span className="token-type">Native</span>
            </div>
            <div className="payment-method-amount">
              ≈ {calculateTokenAmount(amount, undefined)} {networkConfig.nativeCurrency?.symbol}
            </div>
          </div>
          {!selectedToken && (
            <div className="selected-indicator">✓</div>
          )}
        </button>

        {/* Token options */}
        {supportedTokens.map((token) => (
          <button
            key={token.address}
            onClick={() => {
              if (!disabled) {
                onTokenChange(token.address);
                trackPaymentEvent('payment_method_selected', { network, token: token.symbol });
              }
            }}
            className={`payment-method ${selectedToken === token.address ? 'selected' : ''} ${disabled ? 'disabled' : ''}`}
            disabled={disabled}
            aria-label={`Pay with ${token.symbol}`}
          >
            <div className="payment-method-content">
              <div className="payment-method-header">
                <span className="token-symbol">{token.symbol}</span>
                <span className="token-type">Token</span>
              </div>
              <div className="payment-method-amount">
                ≈ {calculateTokenAmount(amount, token.symbol)} {token.symbol}
              </div>
              {token.symbol === 'USDC' || token.symbol === 'USDT' ? (
                <div className="stablecoin-badge">Stablecoin</div>
              ) : null}
            </div>
            {selectedToken === token.address && (
              <div className="selected-indicator">✓</div>
            )}
          </button>
        ))}
      </div>

      <div className="payment-info">
        <p className="tokens-to-receive">
          You will receive: <strong>{tokenAmount.toFixed(2)} tokens</strong>
        </p>
        <p className="price-info">
          Price per token: ${pricePerToken.toFixed(2)} USD
        </p>
      </div>

      <style jsx="true">{`
        .payment-method-selector {
          margin: 1rem 0;
        }

        .payment-method-title {
          font-size: 1rem;
          font-weight: 600;
          margin-bottom: 0.75rem;
          color: var(--text-primary, #111);
        }

        .payment-methods {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
          gap: 0.75rem;
          margin-bottom: 1rem;
        }

        .payment-method {
          position: relative;
          padding: 1rem;
          border: 2px solid var(--border-color, #e5e7eb);
          border-radius: 0.75rem;
          background: var(--bg-primary, #fff);
          cursor: pointer;
          transition: all 0.2s ease;
          text-align: left;
          width: 100%;
        }

        .payment-method:hover:not(.disabled) {
          border-color: var(--accent-color, #6366f1);
          box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
        }

        .payment-method.selected {
          border-color: var(--accent-color, #6366f1);
          background: var(--accent-light, #f3f4ff);
        }

        .payment-method.disabled {
          opacity: 0.5;
          cursor: not-allowed;
        }

        .payment-method-content {
          display: flex;
          flex-direction: column;
          gap: 0.5rem;
        }

        .payment-method-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
        }

        .token-symbol {
          font-size: 1rem;
          font-weight: 700;
          color: var(--text-primary, #111);
        }

        .token-type {
          font-size: 0.75rem;
          color: var(--text-secondary, #666);
          background: var(--bg-secondary, #f3f4f6);
          padding: 0.125rem 0.5rem;
          border-radius: 0.25rem;
        }

        .payment-method-amount {
          font-size: 0.875rem;
          color: var(--text-secondary, #666);
        }

        .stablecoin-badge {
          font-size: 0.75rem;
          color: var(--success-color, #10b981);
          background: var(--success-light, #d1fae5);
          padding: 0.125rem 0.5rem;
          border-radius: 0.25rem;
          width: fit-content;
        }

        .selected-indicator {
          position: absolute;
          top: 0.5rem;
          right: 0.5rem;
          width: 1.5rem;
          height: 1.5rem;
          border-radius: 50%;
          background: var(--accent-color, #6366f1);
          color: white;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 0.875rem;
        }

        .payment-info {
          background: var(--bg-secondary, #f9fafb);
          padding: 1rem;
          border-radius: 0.5rem;
          margin-top: 1rem;
        }

        .payment-info p {
          margin: 0.25rem 0;
          font-size: 0.875rem;
          color: var(--text-secondary, #666);
        }

        .payment-info strong {
          color: var(--text-primary, #111);
          font-weight: 600;
        }

        @media (max-width: 640px) {
          .payment-methods {
            grid-template-columns: 1fr;
          }
        }
      `}</style>
    </div>
  );
};

export default PaymentMethodSelector;